import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { vslDinamica2News, type NovidadeSection } from '@/data/news/vsl-dinamica-2';
import { NovidadeSections } from '@/components/novidades/NovidadeSections';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, X, ArrowLeft, MessageCircle, Sparkles, Inbox, TrendingUp, Film, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotificationsStore } from '@/store/notificationsStore';
import { Skeleton } from '@/components/ui/skeleton';
import { type RecentConversion } from '@/hooks/useRevenueSummary';
import { getPlatformFavicon } from '@/utils/platformLogos';
import frontV2Banner from '@/assets/novidades/front-v2-banner.jpg';
import picosHorarioBanner from '@/assets/novidades/picos-horario.jpg';
import compartilharVideoBanner from '@/assets/novidades/compartilhar-video-banner-v2.jpg';

// ─── Data ───
interface Novidade {
  id: string;
  type: 'novo_recurso' | 'melhoria' | 'correcao' | 'novo_front';
  badge: string;
  badgeColor: string;
  date: string;
  title: string;
  summary: string;
  description: string;
  bullets: string[];
  image?: string;
  footerNote?: string;
  sections?: NovidadeSection[];
}

const NOVIDADES: Novidade[] = [
  vslDinamica2News,
  {
    id: 'nov-2026-09-29-pixel-google-ads',
    type: 'novo_recurso',
    badge: 'Nova integração',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-09-29',
    get title() { return translateInterfaceText("Pixel do Google Ads"); },
    get summary() { return translateInterfaceText("Conecte o Google Ads às suas VSLs e envie as conversões de play, oferta e checkout para as suas campanhas."); },
    get description() { return translateInterfaceText("Agora a HostVSL também fala com o Google Ads. Você cadastra o ID de conversão da sua conta (AW-) e o rótulo de cada ação que quer medir — o play do vídeo, o momento em que a oferta aparece e o clique no botão de compra. A partir daí, você enxerga dentro do próprio Google Ads quais campanhas levam as pessoas a assistir e a clicar na sua oferta."); },
    get bullets() { return [
      translateInterfaceText("Informe o ID de conversão (AW-) e um rótulo para cada ação de conversão criada no Google Ads"),
      translateInterfaceText("Ação de Play obrigatória, disparada no primeiro play do vídeo"),
      translateInterfaceText("Ações de Pitch (quando a oferta aparece) e Checkout (clique no botão de compra) opcionais"),
      translateInterfaceText("Vincule cada pixel a um vídeo e ao site onde a sua VSL está publicada"),
      translateInterfaceText("Configure no menu Pixel, na aba Configurações, no card \"Google Ads\""),
    ]; },
    get footerNote() { return translateInterfaceText("Já disponível no menu Pixel do seu painel."); },
  },
  {
    id: 'nov-2026-09-29-pixel-tiktok',
    type: 'novo_recurso',
    badge: 'Nova integração',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-09-29',
    get title() { return translateInterfaceText("Pixel do TikTok"); },
    get summary() { return translateInterfaceText("Envie os eventos das suas VSLs para o TikTok Ads e otimize as campanhas com base em quem assistiu de verdade."); },
    get description() { return translateInterfaceText("Chegou a integração com o TikTok Ads. Cadastre o ID do pixel e o token de acesso do seu Events Manager e a HostVSL passa a enviar ao TikTok os eventos de quem assiste às suas VSLs: visualização da página, play, retenção e tempo assistido. O envio é feito direto do nosso servidor pela API de eventos do TikTok, então você não precisa colar nenhum código extra na sua página e o seu token fica guardado com segurança."); },
    get bullets() { return [
      translateInterfaceText("Informe o ID do pixel e o token de acesso gerados no Events Manager do TikTok"),
      translateInterfaceText("Envio pelo servidor: visualização da página, play, retenção e tempo assistido"),
      translateInterfaceText("Nenhum código extra na sua página: o token de acesso fica guardado só no servidor"),
      translateInterfaceText("Acompanhe a saúde de cada pixel: registrando, mudo há mais de 24h ou aguardando o 1º disparo"),
      translateInterfaceText("Configure no menu Pixel, na aba Configurações, no card \"TikTok\""),
    ]; },
    get footerNote() { return translateInterfaceText("Já disponível no menu Pixel do seu painel."); },
  },
  {
    id: 'nov-2026-09-08-notificacao-vendas',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-09-08',
    get title() { return translateInterfaceText("Notificação de Vendas"); },
    get summary() { return translateInterfaceText("Mostre avisos de compra por cima da sua VSL e crie prova social enquanto o vídeo roda."); },
    get description() { return translateInterfaceText("Agora você pode exibir avisos de compra por cima da sua VSL, no estilo prova social ao vivo. Você escreve o texto do aviso com nome, cidade e produto, escolhe a plataforma de venda que aparece ao lado e deixa as notificações rodando enquanto o vídeo toca. Dá pra segurar os avisos nos primeiros minutos e só então começar o rodízio, ajustando o intervalo entre eles e quanto tempo cada um fica na tela. E, se quiser, a notificação vira um link clicável que abre a sua oferta em uma nova aba."); },
    get bullets() { return [
      translateInterfaceText("Atraso inicial de até 3 minutos, intervalo de 5 segundos a 5 minutos e duração de 1 a 20 segundos"),
      translateInterfaceText("Escreva o texto com nome, cidade e produto — use a sugestão automática ou a sua própria lista"),
      translateInterfaceText("Mostre a plataforma de venda ao lado do aviso ou envie a sua própria logo"),
      translateInterfaceText("Escolha o canto do vídeo, o tamanho, as cores e o arredondamento, com som de caixa registradora opcional"),
      translateInterfaceText("A notificação pode virar um link clicável que abre a sua oferta em uma nova aba"),
      translateInterfaceText("Ative na personalização do vídeo, na opção \"Notificação de Vendas\""),
    ]; },
    get footerNote() { return translateInterfaceText("Já disponível na personalização dos seus vídeos."); },
  },
  {
    id: 'nov-2026-09-08-contagem-final-inteligente',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-09-08',
    get title() { return translateInterfaceText("Contagem Final Inteligente"); },
    get summary() { return translateInterfaceText("Quando o vídeo termina, um card com contagem regressiva conduz o espectador direto pra sua oferta."); },
    get description() { return translateInterfaceText("Chegou a Contagem Final Inteligente: assim que o vídeo acaba, um card com contagem regressiva aparece e leva o espectador ao próximo passo, sem aquele silêncio depois do último argumento. Você define o tempo da contagem, escreve o título e a descrição do card e decide o desfecho — redirecionar automaticamente para o seu link ou apenas exibir o aviso, deixando a pessoa fechar e voltar ao vídeo. O card ainda combina com a sua marca: cores, tamanho, arredondamento e animação de entrada são todos ajustáveis."); },
    get bullets() { return [
      translateInterfaceText("Contagem regressiva de 3 a 60 segundos ao final do vídeo, sobreposta ao player ou em tela cheia"),
      translateInterfaceText("Escolha o desfecho: redirecionar automaticamente para o seu link ou apenas exibir o aviso"),
      translateInterfaceText("Botão \"Ir Agora\" opcional, pra quem já decidiu e não quer esperar a contagem terminar"),
      translateInterfaceText("Texto próprio no botão e escolha de abrir na mesma aba ou em uma nova"),
      translateInterfaceText("Card com a sua cara: título, descrição, cores, tamanho, arredondamento e animação de entrada"),
      translateInterfaceText("Ative na personalização do vídeo e simule a contagem no preview do player ao lado"),
    ]; },
    get footerNote() { return translateInterfaceText("Termine suas VSLs levando o espectador direto para a decisão."); },
  },
  {
    id: 'nov-2026-09-03-feedback-do-video',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-09-03',
    get title() { return translateInterfaceText("Feedback do Vídeo"); },
    get summary() { return translateInterfaceText("Pergunte a quem assistiu o que achou da sua VSL e receba a nota dentro do próprio player."); },
    get description() { return translateInterfaceText("Sua VSL agora pergunta e escuta: o Feedback do Vídeo é um card de avaliação que aparece dentro do player, no momento que você escolher, e recolhe a opinião de quem assistiu sem tirar a pessoa da página. Você define o formato da nota, o gatilho de exibição, os textos e o visual do card. Na aba Métricas do vídeo você acompanha a nota média, o total de respostas, a distribuição das avaliações e a evolução ao longo do tempo."); },
    get bullets() { return [
      translateInterfaceText("Três formatos de avaliação: emojis, estrelas ou nota de 1 a 5"),
      translateInterfaceText("Escolha quando o card aparece: no final do vídeo, ao pausar ou em um tempo personalizado"),
      translateInterfaceText("Ligue o campo de comentário e peça nome, e-mail e WhatsApp junto com a nota"),
      translateInterfaceText("Ajuste título, subtítulo, texto do botão, mensagem de agradecimento, estilo, cor, posição e tamanho"),
      translateInterfaceText("Na aba Métricas: nota média, total de respostas, distribuição, evolução e os comentários mais recentes"),
      translateInterfaceText("Ative na personalização do vídeo, na categoria Conversão, em \"Feedback do Vídeo\""),
    ]; },
    get footerNote() { return translateInterfaceText("Quem assiste te diz o que mudar na próxima versão."); },
  },
  {
    id: 'nov-2026-08-27-membros',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-08-27',
    get title() { return translateInterfaceText("Membros"); },
    get summary() { return translateInterfaceText("Dê acesso ao seu painel para quem trabalha com você, com permissões definidas recurso por recurso."); },
    get description() { return translateInterfaceText("Sua conta deixou de ser de uma pessoa só. Com Membros, você cadastra quem trabalha com você e define, recurso por recurso, o que cada pessoa pode ver e o que pode editar — ou marca como Administrador e libera todas as áreas de uma vez. Também dá pra liberar apenas as pastas de vídeos que interessam àquela pessoa, deixando o resto da sua biblioteca fora do alcance. Cada um entra com o próprio e-mail e a própria senha, então você para de compartilhar o seu login com o time."); },
    get bullets() { return [
      translateInterfaceText("Cadastre quantas pessoas precisar, cada uma com o próprio e-mail e a própria senha"),
      translateInterfaceText("Defina o nível de cada recurso: sem acesso, visualização ou edição"),
      translateInterfaceText("Permissões agrupadas por área: Vídeos, Conversão, Análises, Otimização, Proteção e Conta"),
      translateInterfaceText("Acesso por pasta: libere só as pastas de vídeos que aquela pessoa precisa"),
      translateInterfaceText("Bloqueie ou remova o acesso na hora em que alguém sair do time"),
      translateInterfaceText("Acesse pelo seu avatar, no canto superior direito, em \"Membros\""),
    ]; },
    get footerNote() { return translateInterfaceText("Seu time trabalhando junto, cada um no seu lugar."); },
  },
  {
    id: 'nov-2026-06-24-jornada-conversao',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-06-24',
    get title() { return translateInterfaceText("Jornada de Conversão"); },
    get summary() { return translateInterfaceText("Coloque um vídeo de entrada antes da sua VSL e um de saída depois, no estilo anúncio do YouTube, pra conduzir o espectador até a próxima ação."); },
    get description() { return translateInterfaceText("Chegou a Jornada de Conversão: agora você encaixa um vídeo de entrada antes da sua VSL principal e, se quiser, um vídeo de saída logo depois — tudo no estilo anúncio do YouTube. Use a entrada pra criar contexto, qualificar ou aquecer o lead antes do conteúdo principal, e a saída pra reforçar a oferta e chamar pra ação assim que o vídeo termina. Os vídeos saem da sua própria biblioteca, com botão de pular, card de CTA clicável e métricas de cada etapa."); },
    get bullets() { return [
      translateInterfaceText("Vídeo de entrada (antes da VSL) e vídeo de saída (depois) — use um ou os dois"),
      translateInterfaceText("Escolha os vídeos direto da sua biblioteca, sem precisar de um novo upload"),
      translateInterfaceText("Botão de pular com contagem regressiva no vídeo de entrada"),
      translateInterfaceText("Card de CTA clicável estilo YouTube: título, descrição, botão e link pra sua oferta"),
      translateInterfaceText("Métricas por etapa: exibições, conclusão, quem pulou, quem chegou ao principal e cliques no CTA"),
      translateInterfaceText("Ative na personalização do vídeo, na opção \"Jornada de Conversão\""),
    ]; },
    get footerNote() { return translateInterfaceText("Já disponível na personalização dos seus vídeos."); },
  },
  {
    id: 'nov-2026-06-07-cloaker-protecao',
    type: 'melhoria',
    badge: 'Melhoria',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-06-07',
    get title() { return translateInterfaceText("Cloaker de VSL reformulado"); },
    get summary() { return translateInterfaceText("Repaginamos a Proteção Anti-Plágio de VSL: ficou mais simples de configurar e de esconder sua oferta de concorrentes e espiões."); },
    get description() { return translateInterfaceText("Reformulamos o Cloaker da HostVSL, a proteção anti-plágio das suas VSLs. Você decide quem pode assistir ao vídeo original por país, região, origem do tráfego (UTM), dispositivo e bloqueio de VPN/Proxy — e quem não se encaixa nas regras é desviado automaticamente para um vídeo alternativo, sem nunca carregar a sua VSL real. Perfeito pra esconder sua oferta de concorrentes, espiões de anúncios e ferramentas de moderação."); },
    get bullets() { return [
      translateInterfaceText("Crie proteções num passo a passo simples, do vídeo às regras"),
      translateInterfaceText("Escolha a VSL protegida e um vídeo alternativo para os acessos bloqueados"),
      translateInterfaceText("Regras por país e região: libere só onde você vende ou bloqueie onde não quer aparecer"),
      translateInterfaceText("Filtros por UTM (origem, campanha, conteúdo, etc.) para barrar tráfego de espionagem"),
      translateInterfaceText("Bloqueio por dispositivo (desktop, celular, tablet) e por VPN/Proxy"),
      translateInterfaceText("Acompanhe quantos acessos cada proteção já bloqueou direto nos cards"),
    ]; },
    get footerNote() { return translateInterfaceText("Já disponível no menu Cloaker do seu painel."); },
  },
  {
    id: 'nov-2026-06-03-tempo-real',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-06-03',
    get title() { return translateInterfaceText("Audiência em Tempo Real"); },
    get summary() { return translateInterfaceText("Veja num mapa-múndi quem está assistindo às suas VSLs agora, em qualquer lugar do mundo."); },
    get description() { return translateInterfaceText("Chegou a Audiência em Tempo Real: um mapa-múndi que mostra, ao vivo, quem está assistindo às suas VSLs neste exato momento e de onde no mundo. Acompanhe quantas pessoas estão na sua oferta agora, quais vídeos estão bombando e de quais fontes de tráfego elas vieram — tudo atualizando sozinho."); },
    get bullets() { return [
      translateInterfaceText("Mapa ao vivo com os espectadores que estão assistindo agora, por cidade e país"),
      translateInterfaceText("Contador de \"assistindo agora\" que se atualiza sozinho a cada poucos segundos"),
      translateInterfaceText("Rankings ao vivo: top vídeos, top lugares (com alternância país/estado) e top fontes de tráfego"),
      translateInterfaceText("Conta apenas quem está realmente assistindo com o som ativo — audiência engajada de verdade"),
      translateInterfaceText("Acesse pelo menu lateral em Análises → Tempo Real"),
    ]; },
    get footerNote() { return translateInterfaceText("Já está disponível no menu Análises do seu painel."); },
  },
  {
    id: 'nov-2026-05-21-google-drive-upload',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-21',
    get title() { return translateInterfaceText("Importar vídeos do Google Drive"); },
    get summary() { return translateInterfaceText("Mande vídeos direto da sua conta do Google Drive pra HostVSL, sem precisar baixar antes."); },
    get description() { return translateInterfaceText("Agora você pode importar vídeos diretamente da sua conta do Google Drive, sem precisar baixar primeiro pro computador. No modal de envio, escolha \"Google Drive\" como origem, conecte sua conta com segurança e selecione os vídeos que quiser mandar pra plataforma."); },
    get bullets() { return [
      translateInterfaceText("No modal de upload, escolha entre \"Upload do computador\" ou \"Google Drive\""),
      translateInterfaceText("Conecte sua conta Google com permissão somente de leitura — não tocamos em nada do seu Drive"),
      translateInterfaceText("Busque e selecione múltiplos vídeos de uma vez"),
      translateInterfaceText("Tamanho máximo de 2GB por vídeo (mesmo limite do upload normal)"),
      translateInterfaceText("Token de acesso vive apenas durante a sessão — segurança em primeiro lugar"),
    ]; },
    get footerNote() { return translateInterfaceText("Disponível no modal \"Importar vídeos\" do seu painel."); },
  },
  {
    id: 'nov-2026-05-21-assistir-na-smart-tv',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-21',
    get title() { return translateInterfaceText("Assistir na Smart TV"); },
    get summary() { return translateInterfaceText("Sua audiência pode transmitir o vídeo pra Chromecast ou AirPlay com um clique direto do player."); },
    get description() { return translateInterfaceText("Adicionamos um novo botão de transmissão para Smart TV nos controles do player. Com ele ativo, seu espectador clica e escolhe transmitir o vídeo pra uma Chromecast (no Chrome) ou Apple TV/AirPlay (no Safari) que esteja na mesma rede Wi-Fi. Ótimo pra ofertas high-ticket onde o cliente quer assistir junto com cônjuge ou sócio antes de fechar."); },
    get bullets() { return [
      translateInterfaceText("Ative na aba \"Controles\" da personalização do vídeo, na opção \"Assistir na Smart TV\""),
      translateInterfaceText("Funciona com Chromecast no Chrome/Edge (Windows, Mac, Android)"),
      translateInterfaceText("Funciona com AirPlay no Safari (iPhone, iPad, Mac)"),
      translateInterfaceText("O botão só aparece pra quem tem um dispositivo compatível na rede — sem poluir o player de quem não usa"),
      translateInterfaceText("Requer que os controles do player estejam habilitados"),
    ]; },
    get footerNote() { return translateInterfaceText("Ativável por vídeo na personalização."); },
  },
  {
    id: 'nov-2026-05-19-player-performance',
    type: 'melhoria',
    badge: 'Melhoria',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-19',
    get title() { return translateInterfaceText("Novo Player de vídeo da HostVSL"); },
    get summary() { return translateInterfaceText("Melhoria de performance completa na abertura dos vídeos — carregamento mais rápido e menos travamentos."); },
    get description() { return translateInterfaceText("Ei, novidade boa por aqui! A gente trabalhou nos bastidores e entregou uma melhoria de performance completa na abertura dos vídeos. Resultado: carregamento mais rápido, menos travamentos e uma experiência muito melhor pra quem assiste o seu conteúdo."); },
    get bullets() { return [
      translateInterfaceText("Carregamento mais rápido no início do vídeo"),
      translateInterfaceText("Menos travamentos durante a reprodução"),
      translateInterfaceText("Experiência mais fluida para a sua audiência"),
      translateInterfaceText("Pra ativar: copie novamente o código do seu vídeo e cole no lugar do antigo, lá onde ele está hospedado"),
      translateInterfaceText("Pronto! Depois disso, sua audiência já passa a aproveitar todas as melhorias"),
    ]; },
    get footerNote() { return translateInterfaceText("Qualquer dúvida no caminho, é só chamar. Estamos aqui pra ajudar."); },
  },
  {
    id: 'nov-2026-05-19-storyboard-player',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-19',
    get title() { return translateInterfaceText("Storyboard"); },
    get summary() { return translateInterfaceText("Sua audiência passa o mouse na timeline e já vê uma miniatura do frame daquele momento."); },
    get description() { return translateInterfaceText("Chegou o Storyboard, um novo recurso dos controles do player. Ao passar o mouse pela barra de progresso, o espectador vê uma miniatura do frame correspondente àquele instante do vídeo. Ajuda a encontrar rapidamente um trecho específico ou voltar pra um ponto de interesse."); },
    get bullets() { return [
      translateInterfaceText("Mini-preview do frame ao passar o mouse na barra de progresso"),
      translateInterfaceText("Ative na aba \"Controles\" da personalização do vídeo, na opção \"Preview na Timeline\""),
      translateInterfaceText("Disponível apenas em vídeos novos — enviados a partir de agora"),
      translateInterfaceText("Pra liberar o recurso em um vídeo antigo, basta refazer o upload: o processamento gera o storyboard automaticamente"),
      translateInterfaceText("Requer que os controles do player estejam habilitados"),
    ]; },
    get footerNote() { return translateInterfaceText("Já está disponível na personalização dos seus novos vídeos."); },
  },
  {
    id: 'nov-2026-04-19-criar-sites-replic',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Criar Sites com a Replic"); },
    get summary() { return translateInterfaceText("Crie e clone páginas em poucos minutos com a Replic, sem cartão de crédito."); },
    get description() { return translateInterfaceText("Conheça a Replic, a nossa ferramenta para criar e clonar sites de alta conversão em poucos minutos. Comece gratuitamente em apenas um clique, sem precisar cadastrar cartão de crédito, e ganhe velocidade para validar suas ofertas."); },
    get bullets() { return [
      translateInterfaceText("Crie sites do zero com blocos prontos e premium"),
      translateInterfaceText("Clone páginas existentes em minutos para acelerar testes"),
      translateInterfaceText("Editor visual intuitivo, sem necessidade de código"),
      translateInterfaceText("Publicação rápida com domínio próprio ou subdomínio"),
      translateInterfaceText("Teste grátis com um clique, sem cartão de crédito"),
    ]; },
    get footerNote() { return translateInterfaceText("Disponível agora no menu Ferramentas."); },
  },
  {
    id: 'nov-2026-04-19-quiz-funnelx',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Quiz Interativo com FunnelX"); },
    get summary() { return translateInterfaceText("Crie quizzes interativos para qualificar leads e aumentar conversão com a FunnelX."); },
    get description() { return translateInterfaceText("A FunnelX é a nossa ferramenta para criar quizzes interativos de alta performance. Engaje sua audiência, qualifique leads e direcione cada pessoa para a oferta certa, com lógica condicional e visual premium."); },
    get bullets() { return [
      translateInterfaceText("Construa quizzes em minutos com editor visual"),
      translateInterfaceText("Lógica condicional para personalizar a jornada de cada lead"),
      translateInterfaceText("Resultados segmentados que aumentam a conversão final"),
      translateInterfaceText("Integração nativa com VSLs e funis da Host VSL"),
      translateInterfaceText("Comece a usar direto pelo menu Ferramentas"),
    ]; },
    get footerNote() { return translateInterfaceText("Transforme curiosidade em conversão."); },
  },
  {
    id: 'nov-2026-04-19-asniper-espionar-ofertas',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Espionar Ofertas com a AdSniper"); },
    get summary() { return translateInterfaceText("Descubra ofertas validadas na Meta com a AdSniper e ganhe 50% OFF com o cupom 50%OFF."); },
    get description() { return translateInterfaceText("Com a AdSniper você espiona em tempo real as ofertas que estão escalando na Meta Ads e descobre rapidamente quais criativos, ângulos e funis estão convertendo. Use o cupom 50%OFF e garanta 50% de desconto em qualquer plano."); },
    get bullets() { return [
      translateInterfaceText("Monitore ofertas validadas que estão rodando na Meta agora"),
      translateInterfaceText("Descubra criativos, ângulos e funis vencedores"),
      translateInterfaceText("Filtros avançados por nicho, formato e tempo no ar"),
      translateInterfaceText("Inspiração direta para suas próximas VSLs e campanhas"),
      translateInterfaceText("Cupom exclusivo 50%OFF para 50% de desconto em qualquer plano"),
    ]; },
    get footerNote() { return translateInterfaceText("Use o cupom 50%OFF e comece hoje mesmo."); },
  },
  {
    id: 'nov-2026-04-19-mini-hooks',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Mini Hooks"); },
    get summary() { return translateInterfaceText("Insira ganchos curtos ao longo do vídeo para aumentar retenção e reengajar o espectador."); },
    get description() { return translateInterfaceText("Mini Hooks são pequenos blocos de texto que aparecem em momentos estratégicos do vídeo para reativar a atenção do espectador. Configure quantos quiser, defina o tempo de exibição e veja o impacto direto na retenção."); },
    get bullets() { return [
      translateInterfaceText("Adicione vários hooks com tempo de início e fim independentes"),
      translateInterfaceText("Edite texto e estilo direto no painel, com preview em tempo real"),
      translateInterfaceText("Animações suaves de entrada e saída sem quebrar a imersão"),
      translateInterfaceText("Combine com Botão de Ação e Headline para reforçar a oferta"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Mantenha o público colado do início ao fim."); },
  },
  {
    id: 'nov-2026-04-19-academy',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Academy com tutoriais"); },
    get summary() { return translateInterfaceText("Acesse uma trilha completa de aulas para dominar todos os recursos da Host VSL."); },
    get description() { return translateInterfaceText("A Academy chegou para acelerar seu domínio da plataforma. Trilha organizada por módulos, com aulas curtas e objetivas sobre cada recurso, do básico ao avançado, com progresso salvo automaticamente."); },
    get bullets() { return [
      translateInterfaceText("Trilha organizada por módulos: Primeiros passos, Personalização, Conversão, Análises e Plano"),
      translateInterfaceText("Progresso salvo por aula e por módulo, com indicadores visuais"),
      translateInterfaceText("Aulas curtas e objetivas com player premium integrado"),
      translateInterfaceText("Busca rápida por aula em qualquer módulo"),
      translateInterfaceText("Layout otimizado para mobile, tablet e desktop"),
    ]; },
    get footerNote() { return translateInterfaceText("Aprenda no seu ritmo, direto do menu Academy."); },
  },
  {
    id: 'nov-2026-04-10-funil-metricas',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-10',
    get title() { return translateInterfaceText("Funil de Conversão nas Métricas"); },
    get summary() { return translateInterfaceText("Visualize toda a jornada do seu público em um funil de barras verticais com taxas de conversão entre etapas."); },
    get description() { return translateInterfaceText("Acompanhe de forma visual e intuitiva cada etapa da jornada do espectador. O funil mostra desde os carregamentos da página até as conversões finais, com percentuais e valores absolutos para cada etapa."); },
    get bullets() { return [
      translateInterfaceText("Visualização em barras verticais proporcionais com gradiente azul progressivo"),
      translateInterfaceText("Cinco etapas do funil: Carregamentos, Visualizações Únicas, Plays Únicos, Cliques no Botão e Conversões"),
      translateInterfaceText("Taxas calculadas automaticamente: Play Rate, Click Rate e Conversion Rate"),
      translateInterfaceText("Efeito de foco ao passar o mouse: destaca a barra selecionada e suaviza as demais"),
      translateInterfaceText("Gráfico de Momento de Conversão no Vídeo para identificar os pontos de maior interesse"),
    ]; },
    get footerNote() { return translateInterfaceText("Entenda onde sua audiência cai e onde a conversão acontece."); },
  },
  {
    id: 'nov-2026-04-10-autoplay-customizar',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-10',
    get title() { return translateInterfaceText("Personalizar Caixa do Autoplay"); },
    get summary() { return translateInterfaceText("Redimensione e reposicione a caixa de autoplay diretamente no player com arraste visual."); },
    get description() { return translateInterfaceText("Agora você pode personalizar a caixa de autoplay de forma visual e intuitiva. Arraste para mover, use os handles para redimensionar e veja as alterações em tempo real no preview do player."); },
    get bullets() { return [
      translateInterfaceText("Arraste a caixa para qualquer posição dentro do player"),
      translateInterfaceText("Redimensione usando os handles nos cantos e laterais"),
      translateInterfaceText("Guias de snap para alinhamento preciso (centro, bordas)"),
      translateInterfaceText("Área segura visual para garantir visibilidade em todos os dispositivos"),
      translateInterfaceText("Todas as alterações refletem no embed final automaticamente"),
    ]; },
    get footerNote() { return translateInterfaceText("Controle total sobre a aparência do seu autoplay."); },
  },
  {
    id: 'nov-2026-04-09-exibicao-dispositivo',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Exibição por Dispositivo"); },
    get summary() { return translateInterfaceText("Controle em quais dispositivos o vídeo será exibido: desktop, tablet ou celular."); },
    get description() { return translateInterfaceText("Configure comportamentos específicos do player dependendo do dispositivo do espectador. Ofereça a melhor experiência em desktop, tablet e mobile de forma automática."); },
    get bullets() { return [
      translateInterfaceText("Personalize o player de forma independente para cada tipo de dispositivo"),
      translateInterfaceText("Otimize a experiência mobile sem comprometer o desktop"),
      translateInterfaceText("Ajustes automáticos de layout e interação por tela"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Entregue a experiência ideal em qualquer tela."); },
  },
  {
    id: 'nov-2026-04-09-legendas',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Legendas"); },
    get summary() { return translateInterfaceText("Adicione arquivos de legenda ao vídeo para melhorar acessibilidade e retenção do público."); },
    get description() { return translateInterfaceText("Adicione legendas aos seus vídeos com suporte a arquivos .VTT e .SRT. Edite textos e tempos diretamente no painel, reordene blocos e escolha entre 4 animações de exibição."); },
    get bullets() { return [
      translateInterfaceText("Upload de arquivos .VTT e .SRT com detecção automática de formato"),
      translateInterfaceText("Edição inline de texto e tempos diretamente no painel"),
      translateInterfaceText("Reordenação por drag-and-drop dos blocos de legenda"),
      translateInterfaceText("Quatro animações disponíveis: Nenhuma, Fade, Pop e Palavra por palavra"),
      translateInterfaceText("Layout responsivo de cápsula central com largura adaptativa por dispositivo"),
    ]; },
    get footerNote() { return translateInterfaceText("Acessibilidade e retenção em um só recurso."); },
  },
  {
    id: 'nov-2026-04-09-recomecar-video',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Recomeçar Vídeo Após o Fim"); },
    get summary() { return translateInterfaceText("Permite reiniciar automaticamente o vídeo assim que ele terminar."); },
    get description() { return translateInterfaceText("Configure o vídeo para retornar automaticamente ao início quando terminar. Com Autoplay ativo, entra em loop contínuo. Caso contrário, fica pausado no início pronto para ser assistido novamente."); },
    get bullets() { return [
      translateInterfaceText("Retorno automático ao segundo 0:00 ao término do vídeo"),
      translateInterfaceText("Loop contínuo quando combinado com Autoplay ativo"),
      translateInterfaceText("Sem replay automático se o Autoplay estiver desativado"),
      translateInterfaceText("Ative com um clique direto no painel de recursos"),
    ]; },
    get footerNote() { return translateInterfaceText("Ideal para vídeos de vitrine, demonstrações e VSLs em loop."); },
  },
  {
    id: 'nov-2026-03-25-pausa-inteligente',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-25',
    get title() { return translateInterfaceText("Pausa Inteligente"); },
    get summary() { return translateInterfaceText("Pausa o vídeo ao sair da aba e retoma ao voltar automaticamente."); },
    get description() { return translateInterfaceText("Agora seus vídeos pausam automaticamente quando o espectador sai da aba do navegador. Ao retornar, a reprodução continua exatamente de onde parou, sem perder nenhum segundo de conteúdo."); },
    get bullets() { return [
      translateInterfaceText("Pausa automática ao detectar que o espectador saiu da aba"),
      translateInterfaceText("Retomada instantânea ao voltar, sem precisar clicar em nada"),
      translateInterfaceText("Respeita pausas manuais: só retoma se foi pausado pela função"),
      translateInterfaceText("Funciona com a Page Visibility API para máxima compatibilidade"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Garanta que cada segundo do seu vídeo seja assistido."); },
  },
  {
    id: 'nov-2026-03-03-compartilhar-video',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-03',
    get title() { return translateInterfaceText("Compartilhar Vídeo com Afiliados"); },
    get summary() { return translateInterfaceText("Compartilhe vídeos com afiliados por link e acompanhe tudo."); },
    get description() { return translateInterfaceText("Agora você pode compartilhar seus vídeos com afiliados por link. O afiliado copia o vídeo direto no painel com um clique, sem precisar baixar no Drive ou fazer upload demorado. Você acompanha quem copiou e toda a atividade do compartilhamento."); },
    get bullets() { return [
      translateInterfaceText("Gere um link compartilhável para qualquer vídeo original do seu painel"),
      translateInterfaceText("Afiliado copia o vídeo com um clique e já pode usar sem upload"),
      translateInterfaceText("Acompanhe cópias por dia, top estados e histórico completo de eventos"),
      translateInterfaceText("Controle o acesso com toggle de ativação, limite de cópias e data de expiração"),
      translateInterfaceText("Exporte o histórico de eventos em CSV para seus relatórios"),
      translateInterfaceText("Vídeos copiados ficam protegidos e não podem ser compartilhados novamente"),
    ]; },
    get footerNote() { return translateInterfaceText("Distribua seus vídeos com velocidade e controle."); },
    image: compartilharVideoBanner,
  },
  {
    id: 'nov-2026-03-03-picos-horario',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-03',
    get title() { return translateInterfaceText("Picos de Horário nas Métricas do Vídeo"); },
    get summary() { return translateInterfaceText("Descubra quando seu público mais assiste seus vídeos."); },
    get description() { return translateInterfaceText("Chegou o recurso Picos de Horário. Agora você identifica com clareza quando seu público mais assiste, em quais dias e em quais faixas de horário."); },
    get bullets() { return [
      translateInterfaceText("Descubra o horário campeão do período com o melhor volume de plays"),
      translateInterfaceText("Compare horários recomendados com ranking de melhores faixas"),
      translateInterfaceText("Veja os horários ideais por tipo de acesso (desktop, mobile, tablet)"),
      translateInterfaceText("Analise o mapa de calor por dia e hora para enxergar padrões"),
      translateInterfaceText("Receba um insight automático com leitura rápida do comportamento"),
    ]; },
    get footerNote() { return translateInterfaceText("Menos achismo, mais performance."); },
    image: picosHorarioBanner,
  },
  {
    id: 'nov-2026-02-24-front-v2',
    type: 'novo_front',
    badge: 'Novo front',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-02-24',
    get title() { return translateInterfaceText("Novo Front Host VSL 2.0"); },
    get summary() { return translateInterfaceText("Visual premium, navegação mais rápida e consistência total."); },
    get description() { return translateInterfaceText("A Host VSL entrou em uma nova fase. O Front 2.0 foi criado para quem quer operar com mais clareza, mais velocidade e mais controle."); },
    get bullets() { return [
      translateInterfaceText("Visual premium e consistente em toda a plataforma"),
      translateInterfaceText("Navegação mais rápida e responsiva em qualquer tela"),
      translateInterfaceText("Experiência mais clara para reduzir erros e acelerar decisões"),
      translateInterfaceText("Componentes e modais com padrão unificado"),
      translateInterfaceText("Base preparada para novas funcionalidades com mais velocidade"),
    ]; },
    get footerNote() { return translateInterfaceText("Alinhada com quem busca escala, performance e operação profissional."); },
    image: frontV2Banner,
  },
];

// ─── Helpers ───
function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr + 'T00:00:00');
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'hoje';
  if (diffDays === 1) return translateInterfaceText("há 1 dia");
  if (diffDays < 30) return `há ${diffDays} dias`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return translateInterfaceText("há 1 mês");
  return `há ${diffMonths} meses`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]}, ${d.getFullYear()}`;
}

// ─── Detail View ───
function NotificationDetail({ novidade, onBack }: { novidade: Novidade; onBack: () => void }) {
  useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleOpenCrisp = () => {
    try { window.$crisp?.push(["do", "chat:open"]); } catch (e) { /* noop */ }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="overflow-y-auto flex-1 min-h-0 px-4 pt-4 pb-2">
        {/* Badge + Date */}
        <div className="flex items-center gap-2.5 mb-2.5">
          <span className={cn('px-2 py-[2px] text-[11px] font-semibold rounded-full leading-none', novidade.badgeColor)}>
            {translateInterfaceText(novidade.badge)}
          </span>
          <span className="text-[11.5px] text-muted-foreground/50">{formatDate(novidade.date)}</span>
        </div>

        {/* Title */}
        <h3 className="text-[17px] font-semibold text-foreground leading-[1.3] mb-2.5">{novidade.title}</h3>

        {/* Image — desktop: after title */}
        {novidade.image && (
          <div
            className="relative aspect-video w-full overflow-hidden rounded-xl my-3 hidden sm:block"
            style={{
              border: '1.5px solid rgba(45, 127, 249, 0.35)',
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
            }}
          >
            {!imageLoaded && <Skeleton className="absolute inset-0 w-full h-full" />}
            <img
              src={novidade.image}
              alt={novidade.title}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              className={cn('w-full h-full object-cover transition-opacity duration-300', imageLoaded ? 'opacity-100' : 'opacity-0')}
            />
          </div>
        )}

        {/* Description */}
        <p className="text-[13.5px] text-foreground/65 leading-[1.6] mb-3">{novidade.description}</p>

        {/* Image — mobile: after description */}
        {novidade.image && (
          <div
            className="relative aspect-video w-full overflow-hidden rounded-xl my-3 sm:hidden"
            style={{
              border: '1.5px solid rgba(45, 127, 249, 0.35)',
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
            }}
          >
            {!imageLoaded && <Skeleton className="absolute inset-0 w-full h-full" />}
            <img
              src={novidade.image}
              alt={novidade.title}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              className={cn('w-full h-full object-cover transition-opacity duration-300', imageLoaded ? 'opacity-100' : 'opacity-0')}
            />
          </div>
        )}

        {/* Mobile: footerNote before bullets */}
        {novidade.footerNote && (
          <p className="text-[12.5px] text-muted-foreground/55 italic leading-snug mb-3 sm:hidden">{novidade.footerNote}</p>
        )}

        {/* Bullets */}
        {novidade.bullets.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {novidade.bullets.slice(0, 5).map((bullet, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-foreground/75 leading-[1.5]">
                <span className="mt-[6px] h-[4.5px] w-[4.5px] rounded-full bg-[#4B7BFF] flex-shrink-0" />
                <span className={novidade.sections ? undefined : "line-clamp-2"}>{bullet}</span>
              </li>
            ))}
          </ul>
        )}

        <NovidadeSections sections={novidade.sections} />

        {/* Desktop: footerNote after bullets */}
        {novidade.footerNote && (
          <p className="text-[12.5px] text-muted-foreground/55 italic leading-snug mb-3 hidden sm:block">{novidade.footerNote}</p>
        )}

        {/* Help CTA */}
        <div className="pt-3 mt-1 border-t border-[#EEF2F8]">
          <div
            className="flex items-center gap-3 px-3 py-2.5 rounded-[10px]"
            style={{ background: 'rgba(75, 123, 255, 0.04)', border: '1px solid rgba(75, 123, 255, 0.10)' }}
          >
            <div className="flex-shrink-0 h-7 w-7 rounded-lg bg-[#4B7BFF]/10 flex items-center justify-center">
              <MessageCircle className="h-3.5 w-3.5 text-[#4B7BFF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-foreground leading-snug">{translateInterfaceText("Ainda tem dúvidas")}</p>
              <p className="text-[11.5px] text-muted-foreground/60 leading-snug">{translateInterfaceText("Fale com nossa equipe.")}</p>
            </div>
            <button
              onClick={handleOpenCrisp}
              className="flex-shrink-0 h-[34px] px-3 rounded-lg text-[12px] font-medium bg-[#4B7BFF] text-white hover:bg-[#3A6AEE] transition-colors duration-150"
            >
              {translateInterfaceText("Chamar no chat")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCurrencyFull(value: number): string {
  return new Intl.NumberFormat(getInterfaceLanguage(), { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(value);
}

function timeAgoFull(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'agora mesmo';
  if (diffMins < 60) return `há ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `há ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return translateInterfaceText("há 1 dia");
  if (diffDays < 30) return `há ${diffDays} dias`;
  return `há ${Math.floor(diffDays / 30)} meses`;
}

// ─── Video Notification ───
export interface ProcessedVideoNotification {
  id_video: string;
  nomeVideo: string;
  frame?: string;
  processingCompletedAt?: string;
  processingStatus?: string;
}

// ─── Main Component ───
interface NotificationsMenuProps {
  recentConversions?: RecentConversion[];
  recentlyReadyVideos?: ProcessedVideoNotification[];
}

export function NotificationsMenu({ recentConversions = [], recentlyReadyVideos = [] }: NotificationsMenuProps) {
  useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'novidades' | 'vendas' | 'videos'>('novidades');
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const { readIds, markAsRead, markAllAsRead, isRead } = useNotificationsStore();

  const unreadNovidadesCount = NOVIDADES.filter((n) => !readIds.includes(n.id)).length;
  const unreadVideosCount = recentlyReadyVideos.filter((v) => !isRead(`video-ready-${v.id_video}`)).length;
  const unreadCount = unreadNovidadesCount
    + (recentConversions.length > 0 && !readIds.includes('vendas-seen') ? 1 : 0)
    + unreadVideosCount;
  const selectedNovidade = selectedId ? NOVIDADES.find((n) => n.id === selectedId) : null;

  // Fecha ao clicar fora
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSelectedId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ESC para fechar
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setIsOpen(false); setSelectedId(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleItemClick = (novidade: Novidade) => {
    markAsRead(novidade.id);
    setSelectedId(novidade.id);
  };

  const handleMarkAllRead = () => {
    markAllAsRead([
      ...NOVIDADES.map((n) => n.id),
      'vendas-seen',
      ...recentlyReadyVideos.map((v) => `video-ready-${v.id_video}`),
    ]);
  };

  const handleTabChange = (tab: 'novidades' | 'vendas' | 'videos') => {
    setActiveTab(tab);
    setSelectedId(null);
    if (tab === 'vendas') {
      markAsRead('vendas-seen');
    }
    if (tab === 'videos') {
      recentlyReadyVideos.forEach((v) => markAsRead(`video-ready-${v.id_video}`));
    }
  };

  return (
    <>
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={() => { setIsOpen(!isOpen); if (isOpen) setSelectedId(null); }}
        className={cn(
          "relative h-[42px] w-[42px] min-h-[42px] min-w-[42px] rounded-[10px] flex items-center justify-center group",
          "hover:bg-[#F1F5FF] transition-all duration-150",
          isOpen && "bg-[#F1F5FF]"
        )}
        aria-label={translateInterfaceText("Novidades")}
      >
        <Bell className="h-[20px] w-[20px] text-[#667085] group-hover:text-[#315FD5] transition-colors duration-150" strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4B7BFF] border-2 border-white text-[10px] font-bold text-white flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && createPortal(
        <>
          {/* Backdrop sutil */}
          <div
            className="fixed inset-0 z-[90] bg-black/[0.08]"
            onClick={() => { setIsOpen(false); setSelectedId(null); }}
          />

          <div
            ref={panelRef}
            className={cn(
              "fixed z-[91] flex flex-col",
              "bg-background rounded-2xl overflow-hidden",
              "animate-in fade-in-0 slide-in-from-top-2 duration-200",
              // Mobile: centralizado. Com 396px fixos ancorados em right:24px o
              // painel estourava pela esquerda em telas estreitas (360px).
              "left-1/2 -translate-x-1/2",
              // Desktop: volta a ancorar na direita, sob o sino.
              "lg:left-auto lg:right-6 lg:translate-x-0"
            )}
            style={{
              top: '72px',
              width: 'min(396px, calc(100vw - 24px))',
              ...(selectedNovidade
                ? { height: '365px' }
                : { maxHeight: '365px' }
              ),
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.14), 0 4px 16px rgba(15, 23, 42, 0.06)',
              border: '1px solid hsl(220, 25%, 93%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 h-[48px] flex-shrink-0 bg-[#4B7BFF] rounded-t-2xl">
              <div className="flex items-center gap-2">
                {selectedNovidade && (
                  <button
                    onClick={() => setSelectedId(null)}
                    className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-white/15 transition-colors mr-0.5"
                  >
                    <ArrowLeft className="h-4 w-4 text-white" />
                  </button>
                )}
                <Sparkles className="h-4 w-4 text-white/80" />
                <h2 className="text-[14.5px] font-medium text-white">{translateInterfaceText("Últimas atualizações")}</h2>
                {!selectedNovidade && unreadCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-white/20 text-[11px] font-semibold text-white leading-none">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!selectedNovidade && unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[11.5px] text-white/80 font-medium hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/10"
                  >
                    {translateInterfaceText("Marcar como lidas")}</button>
                )}
                <button
                  onClick={() => { setIsOpen(false); setSelectedId(null); }}
                  className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-white/15 transition-colors"
                >
                  <X className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            {!selectedNovidade && (
              <div className="flex items-center gap-1 px-3 pt-2 pb-0 flex-shrink-0">
                <button
                  onClick={() => handleTabChange('novidades')}
                  className={cn(
                    'flex items-center gap-1.5 h-[30px] px-3 rounded-lg text-[12px] font-semibold transition-all duration-150',
                    activeTab === 'novidades'
                      ? 'bg-[#4B7BFF]/10 text-[#4B7BFF]'
                      : 'text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {translateInterfaceText("Novidades")}{unreadNovidadesCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#4B7BFF] text-[9px] font-bold text-white leading-none">
                      {unreadNovidadesCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleTabChange('vendas')}
                  className={cn(
                    'flex items-center gap-1.5 h-[30px] px-3 rounded-lg text-[12px] font-semibold transition-all duration-150',
                    activeTab === 'vendas'
                      ? 'bg-[#4B7BFF]/10 text-[#4B7BFF]'
                      : 'text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  {translateInterfaceText("Vendas")}{recentConversions.length > 0 && !isRead('vendas-seen') && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#4B7BFF] text-[9px] font-bold text-white leading-none">
                      {translateInterfaceText("novo")}</span>
                  )}
                </button>
                <button
                  onClick={() => handleTabChange('videos')}
                  className={cn(
                    'flex items-center gap-1.5 h-[30px] px-3 rounded-lg text-[12px] font-semibold transition-all duration-150',
                    activeTab === 'videos'
                      ? 'bg-[#4B7BFF]/10 text-[#4B7BFF]'
                      : 'text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  <Film className="h-3.5 w-3.5" />
                  {translateInterfaceText("Vídeos")}{unreadVideosCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#4B7BFF] text-[9px] font-bold text-white leading-none">
                      {unreadVideosCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Content */}
            {selectedNovidade ? (
              <NotificationDetail novidade={selectedNovidade} onBack={() => setSelectedId(null)} />
            ) : activeTab === 'videos' ? (
              /* Aba Vídeos */
              <div className="flex-1 min-h-0 overflow-y-auto">
                {recentlyReadyVideos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-6 text-center py-10">
                    <div className="h-11 w-11 rounded-xl bg-[#4B7BFF]/[0.08] flex items-center justify-center mb-3">
                      <Film className="h-5 w-5 text-[#4B7BFF]/40" />
                    </div>
                    <h3 className="text-[14.5px] font-semibold text-foreground mb-1">{translateInterfaceText("Nenhum vídeo finalizado")}</h3>
                    <p className="text-[12.5px] text-muted-foreground/55 leading-relaxed max-w-[240px]">{translateInterfaceText("Quando um vídeo terminar o processamento, você verá aqui e poderá editá-lo.")}</p>
                  </div>
                ) : (
                  <div className="px-2 py-2 flex flex-col gap-[2px]">
                    {recentlyReadyVideos.map((video) => {
                      const read = isRead(`video-ready-${video.id_video}`);
                      const isFailed = video.processingStatus === 'failed_permanent';
                      const accent = isFailed ? '#EF4444' : '#4B7BFF';
                      return (
                        <div
                          key={video.id_video}
                          className={cn(
                            "rounded-xl px-3 py-[10px] flex items-start gap-3 border border-transparent",
                            !read && (isFailed
                              ? "bg-[#EF4444]/[0.03] border-[#EF4444]/10"
                              : "bg-[#4B7BFF]/[0.03] border-[#4B7BFF]/10")
                          )}
                        >
                          {/* Thumbnail ou ícone */}
                          <div
                            className={cn(
                              "flex-shrink-0 h-9 w-9 rounded-[10px] overflow-hidden mt-0.5 flex items-center justify-center",
                              isFailed ? "bg-[#EF4444]/[0.08]" : "bg-[#4B7BFF]/[0.08]"
                            )}
                          >
                            {video.frame ? (
                              <img src={video.frame} alt={video.nomeVideo} className="h-full w-full object-cover" />
                            ) : (
                              <Film
                                className={cn(
                                  "h-4 w-4",
                                  isFailed ? "text-[#EF4444]/60" : "text-[#4B7BFF]/60"
                                )}
                              />
                            )}
                          </div>
                          {/* Conteúdo */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-[3px]">
                              {isFailed ? (
                                <XCircle className="h-3 w-3 text-[#EF4444] flex-shrink-0" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3 text-[#4B7BFF] flex-shrink-0" />
                              )}
                              <span
                                className="text-[11px] font-semibold leading-none"
                                style={{ color: accent }}
                              >
                                {isFailed ? translateInterfaceText("Erro: Suba novamente o vídeo") : translateInterfaceText("Pronto para editar")}
                              </span>
                              {video.processingCompletedAt && (
                                <>
                                  <span className="text-muted-foreground/20 text-[10px]">·</span>
                                  <span className="text-[11px] text-muted-foreground/60 leading-none">{timeAgoFull(video.processingCompletedAt)}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                {!read && (
                                  <span
                                    className="h-[6px] w-[6px] rounded-full flex-shrink-0"
                                    style={{ backgroundColor: accent }}
                                  />
                                )}
                                <p className={cn("text-[13px] leading-snug line-clamp-1", !read ? "font-bold text-foreground" : "font-medium text-foreground/75")}>
                                  {video.nomeVideo}
                                </p>
                              </div>
                              {!isFailed && (
                                <button
                                  onClick={() => {
                                    markAsRead(`video-ready-${video.id_video}`);
                                    setIsOpen(false);
                                    navigate(`/personalizations/${video.id_video}`);
                                  }}
                                  className="flex-shrink-0 h-[28px] px-2.5 rounded-lg text-[11px] font-semibold bg-[#4B7BFF]/10 text-[#4B7BFF] hover:bg-[#4B7BFF]/20 transition-colors duration-150"
                                >
                                  {translateInterfaceText("Editar")}</button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : activeTab === 'novidades' ? (
              <div className="flex-1 min-h-0 overflow-y-auto">
                {NOVIDADES.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-6 text-center">
                    <div className="h-11 w-11 rounded-xl bg-[#4B7BFF]/[0.08] flex items-center justify-center mb-3">
                      <Inbox className="h-5 w-5 text-[#4B7BFF]/40" />
                    </div>
                    <h3 className="text-[14.5px] font-semibold text-foreground mb-1">{translateInterfaceText("Nenhuma atualização por aqui")}</h3>
                    <p className="text-[12.5px] text-muted-foreground/55 leading-relaxed max-w-[240px]">{translateInterfaceText("Quando houver novidades, elas aparecem neste painel.")}</p>
                  </div>
                ) : (
                  <div className="px-2 py-2 flex flex-col gap-[2px]">
                    {NOVIDADES.map((novidade) => {
                      const read = isRead(novidade.id);
                      return (
                        <button
                          key={novidade.id}
                          onClick={() => handleItemClick(novidade)}
                          className={cn(
                            "w-full text-left rounded-xl px-3 py-[10px] flex items-start gap-3 transition-all duration-150 group",
                            "hover:bg-[#4B7BFF]/[0.05] hover:shadow-[0_1px_3px_rgba(75,123,255,0.06)] cursor-pointer",
                            "border border-transparent hover:border-[#4B7BFF]/10",
                            !read && "bg-[#4B7BFF]/[0.03]"
                          )}
                        >
                          <div className="flex-shrink-0 h-9 w-9 rounded-[10px] flex items-center justify-center mt-0.5 bg-[#4B7BFF]/[0.08]">
                            <Sparkles className="h-4 w-4 text-[#4B7BFF]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-[3px]">
                              <span className="text-[11px] text-muted-foreground/70 font-medium leading-none">{timeAgo(novidade.date)}</span>
                              <span className="text-muted-foreground/20 text-[10px]">·</span>
                              <span className="inline-flex items-center h-[18px] px-1.5 text-[10px] font-semibold rounded-full leading-none bg-[#4B7BFF]/10 text-[#4B7BFF]">
                                {translateInterfaceText(novidade.badge)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {!read && <span className="h-[6px] w-[6px] rounded-full bg-[#4B7BFF] flex-shrink-0" />}
                              <h4 className={cn("text-[14px] leading-snug line-clamp-1", !read ? "font-semibold text-foreground" : "font-medium text-foreground/75")}>
                                {novidade.title}
                              </h4>
                            </div>
                            <p className="text-[12.5px] text-muted-foreground/70 leading-snug line-clamp-1 mt-[2px]">{novidade.summary}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* Aba Vendas */
              <div className="flex-1 min-h-0 overflow-y-auto">
                {recentConversions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-6 text-center py-10">
                    <div className="h-11 w-11 rounded-xl bg-[#22c55e]/[0.08] flex items-center justify-center mb-3">
                      <TrendingUp className="h-5 w-5 text-[#22c55e]/40" />
                    </div>
                    <h3 className="text-[14.5px] font-semibold text-foreground mb-1">{translateInterfaceText("Nenhuma venda registrada")}</h3>
                    <p className="text-[12.5px] text-muted-foreground/55 leading-relaxed max-w-[240px]">{translateInterfaceText("Configure o rastreamento de conversões para ver suas vendas aqui.")}</p>
                  </div>
                ) : (
                  <div className="px-2 py-2 flex flex-col gap-[2px]">
                    {recentConversions.map((conv) => (
                      <div
                        key={conv.id}
                        className="rounded-xl px-3 py-[10px] flex items-start gap-3 border border-transparent"
                      >
                        {/* Ícone */}
                        <div className="flex-shrink-0 h-9 w-9 rounded-[10px] flex items-center justify-center mt-0.5 bg-[#4B7BFF]/[0.08]">
                          {conv.platformName && getPlatformFavicon(conv.platformName) ? (
                            <img src={getPlatformFavicon(conv.platformName)} alt={conv.platformName} className="h-9 w-9 object-cover rounded-[10px]" />
                          ) : (
                            <TrendingUp className="h-4 w-4 text-[#4B7BFF]" />
                          )}
                        </div>
                        {/* Conteúdo */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-[2px]">
                            <span className="text-[13px] font-bold text-[#4B7BFF]">{formatCurrencyFull(conv.amount)}</span>
                            <span className="text-[11px] text-muted-foreground/60 flex-shrink-0">{timeAgoFull(conv.createdAt)}</span>
                          </div>
                          {conv.videoTitle && (
                            <p className="text-[12px] text-foreground/70 leading-snug line-clamp-1">{conv.videoTitle}</p>
                          )}
                          {conv.platformName && (
                            <p className="text-[11px] text-muted-foreground/50 leading-snug mt-[1px]">{conv.platformName} · {conv.eventType}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
